import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import useCustomFetchApi from '../utils/customFetchApi';
import { getCurrentChannel } from '../config';
import { trackEvent } from '../utils/analytics';
import Notification from './Notification';
import ProfilePreviewModal from './ProfilePreviewModal';
import ShareModal from './ShareModal';
import Footer from './Footer';
import './Dashboard.scss';

const Dashboard = () => {
  const { user, token, updateUser, logout } = useAuth();
  const { theme } = useTheme();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [notification, setNotification] = useState({ message: '', type: '', visible: false });
  const [profile, setProfile] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [spotPrice, setSpotPrice] = useState(null);
  const [isLoadingSpotPrice, setIsLoadingSpotPrice] = useState(false);
  const [countryList, setCountryList] = useState([]);
  const [detectedCountry, setDetectedCountry] = useState(null);
  const [profileForm, setProfileForm] = useState({
    fullName: '',
    nickname: '',
    country: '',
    gender: '',
    dateOfBirth: '',
    bio: '',
    isEnabled: true
  });

  const showNotification = useCallback((message, type = 'info') => {
    setNotification({ message, type, visible: true });
    setTimeout(() => {
      setNotification(prev => ({ ...prev, visible: false }));
    }, 4000);
  }, []);

  const api = useCustomFetchApi(showNotification);

  const switchLanguageFromMenu = (langKey) => {
    const langCode = langKey === 'us' ? 'en' : langKey; // map display key to code
    try { i18n.changeLanguage(langCode); } catch (_) {}
    const segments = (location.pathname || '/').split('/').filter(Boolean);
    const known = ['en', 'ar', 'my'];
    if (segments.length && known.includes(segments[0])) segments.shift();
    const newPath = `/${langCode}${segments.length ? '/' + segments.join('/') : ''}`;
    
    // Track language switch
    trackEvent('language_switched', {
      from_language: i18n.language,
      to_language: langCode,
      source: 'dashboard_menu',
      channel: getCurrentChannel()
    });
    
    navigate(newPath);
    setIsMenuOpen(false);
  };

  // Fetch user profile on component mount
  useEffect(() => {
    if (user?.uuid && token) {
      fetchUserProfile();
      // Track dashboard entry
      trackEvent('dashboard_viewed', {
        user_id: user.uuid,
        channel: getCurrentChannel()
      });
    }
  }, [user?.uuid, token]);

  // Auto-enable editing mode for new users (no profile)
  useEffect(() => {
    if (!isLoading && !profile && user?.uuid) {
//      console.log('🔧 Auto-enabling editing mode for new user');
      setIsEditing(true);
      // Set initial form data for new user
      setProfileForm({
        fullName: user?.name || '',
        nickname: user?.email?.split('@')[0] || '', // Use email prefix as default nickname
        country: user?.country || detectedCountry || '', // Use detected country as default
        gender: '',
        dateOfBirth: '',
        bio: '',
        isEnabled: true
      });
    }
  }, [isLoading, profile, user?.uuid, detectedCountry]);

  // Fetch spot price on component mount
  useEffect(() => {
    fetchSpotPrice();
  }, []);

  // Fetch country list and detect user's country
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await api.get('/countries');
        if (response && Array.isArray(response.countries)) {
          setCountryList(response.countries);
          
          // Set detected country from API response
          if (response.selected?.code) {
            setDetectedCountry(response.selected.code);
            
            // If user has no country set, immediately pre-select the detected one
            if (!profile?.country && !profileForm.country) {
              setProfileForm(prev => ({
                ...prev,
                country: response.selected.code
              }));
              console.log('🌍 Auto-detected and pre-selected country:', response.selected.code);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching countries:', error);
      }
    };

    fetchCountries();
  }, []);

  // Ensure body scroll is enabled when Dashboard loads
  useEffect(() => {
    // Remove any modal-open class that might be stuck on body
    document.body.classList.remove('modal-open');
    
    // Ensure body is scrollable
    document.body.style.overflow = 'auto';
    document.body.style.position = 'static';
    document.body.style.width = 'auto';
    
    // Cleanup on unmount
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, []);

  // Ensure scroll is available when editing mode changes
  useEffect(() => {
    if (isEditing) {
      // Force scroll to be enabled when editing
      document.body.style.overflow = 'auto';
      document.body.style.position = 'static';
      document.body.style.width = 'auto';
      document.body.classList.remove('modal-open');
      
      // Scroll to the form area to make it visible
      setTimeout(() => {
        const profileSection = document.querySelector('.profile-section');
        if (profileSection) {
          profileSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  }, [isEditing]);

  // Fetch user profile
  const fetchUserProfile = async () => {
    try {
      if (!user?.uuid) {
        showNotification('User UUID not found', 'error');
        return;
      }

      setIsLoading(true);
      const response = await api.get(`/profile/user/${user.uuid}`);
      if (response) {
        setProfile(response);
        
        // Convert date_of_birth from ISO timestamp to YYYY-MM-DD format
        let formattedDate = '';
        if (response.date_of_birth) {
          try {
            const date = new Date(response.date_of_birth);
            formattedDate = date.toISOString().split('T')[0]; // Extract YYYY-MM-DD
          } catch (e) {
            console.error('Error parsing date_of_birth:', e);
          }
        }
        
        setProfileForm({
          fullName: response.name || '',
          nickname: response.nickname || '',
          country: response.country || '', // Use saved country from API, don't override with detected
          gender: response.gender || '',
          dateOfBirth: formattedDate,
          bio: response.message || '', // Use message field as bio from API
          isEnabled: response.is_public !== undefined ? response.is_public : true
        });
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
//      console.log('Error status:', error.status);
//      console.log('Error message:', error.message);
      
      // Check if it's a 404 (user not found) or other error
      if (error.status === 404) {
        showNotification(t('dashboard.welcomeNew'), 'info');
      } else {
        showNotification('Failed to load profile', 'error');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch spot price
  const fetchSpotPrice = async () => {
    try {
      setIsLoadingSpotPrice(true);
      const currentChannel = getCurrentChannel();
      const response = await api.get(`/spot-price?channel=${currentChannel}`);
      if (response) {
        setSpotPrice(response);
      }
    } catch (error) {
      console.error('Error fetching spot price:', error);
      // Don't show error notification for spot price as it's not critical
    } finally {
      setIsLoadingSpotPrice(false);
    }
  };

  // Update or create profile
  const handleUpdateProfile = async () => {
//    console.log('🔄 Profile update initiated');
//    console.log('Profile:', profile);
//    console.log('User:', user);
//    console.log('Form data:', profileForm);
    
    // Validate required fields
    if (!profileForm.fullName || !profileForm.fullName.trim()) {
      showNotification('Full Name is required', 'error');
      return;
    }
    
    if (!profileForm.nickname || !profileForm.nickname.trim()) {
      showNotification('Nickname is required', 'error');
      return;
    }
    
    if (!profileForm.country) {
      showNotification('Country is required', 'error');
      return;
    }
    
    if (!profileForm.gender) {
      showNotification('Gender is required', 'error');
      return;
    }
    
    if (!profileForm.dateOfBirth) {
      showNotification('Date of Birth is required', 'error');
      return;
    }
    
    if (!profileForm.bio || !profileForm.bio.trim()) {
      showNotification('Bio is required', 'error');
      return;
    }
    
    const profileUuid = profile?.uuid || user?.uuid;
    if (!profileUuid) {
      console.error('❌ No UUID found');
      showNotification('UUID not found', 'error');
      return;
    }

//    console.log('📝 Using UUID:', profileUuid);
    
    try {
      setIsUpdatingProfile(true);
//      console.log('🚀 Sending request to:', `/profile/${profileUuid}`);
      
      // Use user UUID if no profile exists yet (for new users after payment)
      const response = await api.put(`/profile/${profileUuid}`, {
        name: profileForm.fullName,
        nickname: profileForm.nickname,
        country: profileForm.country, // Include country in update
        gender: profileForm.gender, // Include gender in update
        date_of_birth: profileForm.dateOfBirth, // Include date of birth in update
        message: profileForm.bio, // Send bio as message field to match API
        is_public: profileForm.isEnabled // Include profile visibility toggle (send as is_public)
      });
      
//      console.log('✅ Profile update response:', response);

      if (response && response.success) {
        // Track profile edit success
        trackEvent('profile_edited', {
          user_id: profileUuid,
          fields_updated: Object.keys(profileForm).filter(key => profileForm[key] !== (profile ? profile[key] : '')),
          channel: getCurrentChannel()
        });
        
        // For new profile creation, set the profile data manually since /user endpoint might not be ready yet
        if (!profile) {
          setProfile({
            uuid: profileUuid,
            name: profileForm.fullName,
            nickname: profileForm.nickname,
            message: profileForm.bio,
            country: profileForm.country, // Use the country from the form
            gender: profileForm.gender,
            date_of_birth: profileForm.dateOfBirth,
            is_public: profileForm.isEnabled
          });
        } else {
          // For existing profile updates, fetch fresh data
          try {
            await fetchUserProfile();
          } catch (error) {
            // If fetch fails, update local state manually
            console.warn('Failed to fetch updated profile, updating locally:', error);
            setProfile(prev => ({
              ...prev,
              name: profileForm.fullName,
              nickname: profileForm.nickname,
              country: profileForm.country, // Update country in local state
              gender: profileForm.gender,
              date_of_birth: profileForm.dateOfBirth,
              message: profileForm.bio,
              is_public: profileForm.isEnabled
            }));
          }
        }
        
        setIsEditing(false);
        showNotification('Profile updated successfully', 'success');
      } else {
        showNotification('Profile update failed', 'error');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      showNotification('Failed to update profile', 'error');
      // Track profile edit failure
      trackEvent('profile_edit_failed', {
        user_id: profile?.uuid,
        error: error.message,
        channel: getCurrentChannel()
      });
    } finally {
      setIsUpdatingProfile(false);
    }
  };

  // Handle file input change for profile image
  const handleProfileImageChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const profileUuid = profile?.uuid || user?.uuid;
    if (!profileUuid) {
      showNotification('UUID not found', 'error');
      return;
    }

    try {
      setIsUploadingImage(true);
      const formData = new FormData();
      formData.append('image', file);

      const response = await api.post(`/profile/${profileUuid}/image`, formData);

      if (response) {
        showNotification('Profile image updated successfully', 'success');
        // Track image upload success
        trackEvent('profile_image_uploaded', {
          user_id: profile.uuid,
          file_type: file.type,
          file_size: file.size,
          channel: getCurrentChannel()
        });
        // Refresh profile to get new image
        await fetchUserProfile();
      }
    } catch (error) {
      console.error('Error uploading profile image:', error);
      showNotification('Failed to upload profile image', 'error');
      // Track image upload failure
      trackEvent('profile_image_upload_failed', {
        user_id: profile?.uuid,
        error: error.message,
        channel: getCurrentChannel()
      });
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleFormChange = (field, value) => {
    setProfileForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Generate profile share URL
  const generateProfileUrl = () => {
    if (!profile?.uuid) return '';
    const currentLang = i18n.language || 'en';
    // Use channel from profile API response, fallback to getCurrentChannel()
    const profileChannel = profile?.channel || getCurrentChannel();
    const baseUrl = window.location.origin;
    return `${baseUrl}/${currentLang}/${profileChannel}/profile/${profile.uuid}`;
  };

  return (
    <div className={`dashboard ${theme}`}>
      {/* Menu button moved outside header to avoid stacking context issues */}
      <button
        className={`menu-button ${isMenuOpen ? 'open' : ''}`}
        aria-label="Open menu"
        onClick={() => {
          setIsMenuOpen(v => !v);
          // Track menu toggle
          trackEvent('dashboard_menu_toggled', {
            opened: !isMenuOpen,
            user_id: profile?.uuid,
            channel: getCurrentChannel()
          });
        }}
      >
        <span />
        <span />
        <span />
      </button>
      
      {isMenuOpen && (
        <>
          <div className="menu-overlay" onClick={() => setIsMenuOpen(false)} />
          <div className="menu-panel">
            <div className="menu-section">
              <h4>{t('dashboard.menu.language', 'Language')}</h4>
              <div className="menu-language-row">
                <button type="button" className="menu-link menu-language-row" onClick={() => switchLanguageFromMenu('us')}>US</button>
                <button type="button" className="menu-link menu-language-row" onClick={() => switchLanguageFromMenu('my')}>MY</button>
              </div>
            </div>
            <div className="menu-section">
              <h4>{t('dashboard.menu.account', 'Account')}</h4>
              <button type="button" className="menu-link" onClick={() => { 
                const currentLang = i18n.language || 'en';
                // Track navigation to home
                trackEvent('dashboard_navigate_home', {
                  user_id: profile?.uuid,
                  channel: 'malaysia'
                });
                // Always navigate to malaysia landing page with image grid
                navigate(`/${currentLang}/malaysia`); 
                setIsMenuOpen(false); 
              }}>
                {t('dashboard.menu.goToHome', 'Go to Home')}
              </button>
              <button type="button" className="menu-link" onClick={() => { 
                // Track logout
                trackEvent('user_logout', {
                  user_id: profile?.uuid,
                  source: 'dashboard_menu',
                  channel: getCurrentChannel()
                });
                logout(); 
                setIsMenuOpen(false); 
              }}>
                {t('dashboard.menu.logout', 'Logout')}
              </button>
            </div>
          </div>
        </>
      )}

      <div className="dashboard-container">
        {/* Dashboard Header */}
        <header className="dashboard-header">
          <h1>{t('dashboard.title', 'Dashboard')}</h1>
          <div className="welcome-message">
            {t('dashboard.welcome', 'Welcome')}, {profile?.nickname || profile?.name || user?.email?.split('@')[0] || t('dashboard.user', 'User')}!
          </div>
        </header>

        {/* Spot Price Section */}
        <section className="spot-price-section">
          <div className="spot-price-card">
            <div className="spot-price-header">
              <h3>{t('dashboard.currentSpotPrice', 'Current Spot Price')}</h3>
            </div>
            <div className="spot-price-content">
              {isLoadingSpotPrice ? (
                <div className="spot-price-loading">
                  <div className="loading-spinner"></div>
                  <span>{t('dashboard.loadingPrice', 'Loading price...')}</span>
                </div>
              ) : spotPrice ? (
                <div className="spot-price-display">
                  <div className="price-value">
                    <span className="currency">
                      {(() => {
                        const currency = spotPrice.dataValues?.currency || spotPrice.currency || 'USD';
                        switch(currency.toUpperCase()) {
                          case 'USD': return '$';
                          case 'EUR': return '€';
                          case 'GBP': return '£';
                          case 'JPY': return '¥';
                          case 'CNY': return '¥';
                          case 'KRW': return '₩';
                          case 'INR': return '₹';
                          case 'RUB': return '₽';
                          case 'BRL': return 'R$';
                          case 'CAD': return 'C$';
                          case 'AUD': return 'A$';
                          case 'CHF': return 'CHF';
                          case 'SEK': return 'kr';
                          case 'NOK': return 'kr';
                          case 'DKK': return 'kr';
                          case 'PLN': return 'zł';
                          case 'CZK': return 'Kč';
                          case 'HUF': return 'Ft';
                          case 'TRY': return '₺';
                          case 'ZAR': return 'R';
                          case 'MXN': return '$';
                          case 'SGD': return 'S$';
                          case 'HKD': return 'HK$';
                          case 'NZD': return 'NZ$';
                          case 'THB': return '฿';
                          case 'MYR': return 'RM';
                          case 'IDR': return 'Rp';
                          case 'PHP': return '₱';
                          case 'VND': return '₫';
                          case 'IQD': return 'ع.د';
                          case 'SAR': return 'ر.س';
                          case 'AED': return 'د.إ';
                          case 'KWD': return 'د.ك';
                          case 'BHD': return 'د.ب';
                          case 'OMR': return 'ر.ع';
                          case 'QAR': return 'ر.ق';
                          case 'JOD': return 'د.أ';
                          case 'LBP': return 'ل.ل';
                          case 'EGP': return 'ج.م';
                          case 'MAD': return 'د.م';
                          case 'DZD': return 'د.ج';
                          case 'TND': return 'د.ت';
                          case 'LYD': return 'د.ل';
                          default: return currency;
                        }
                      })()}
                    </span>
                    <span className="amount">{spotPrice.dataValues?.spot_price || spotPrice.spot_price || 'N/A'}</span>
                  </div>
                  {(spotPrice.dataValues?.created_at || spotPrice.created_at) && (
                    <div className="price-updated">
                      {t('dashboard.lastUpdated', 'Last updated')}: {new Date(spotPrice.dataValues?.created_at || spotPrice.created_at).toLocaleString()}
                    </div>
                  )}
                </div>
              ) : (
                <div className="spot-price-error">
                  <span>{t('dashboard.priceUnavailable', 'Price unavailable')}</span>
                  <button onClick={fetchSpotPrice} className="retry-button">
                    {t('dashboard.retry', 'Retry')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Profile Section */}
        <section className="profile-section">
          <h2>{t('dashboard.profile', 'Profile')}</h2>
          <div className="profile-card">
            <div className="profile-image">
              {profile?.images && profile.images.length > 0 ? (
                <img src={profile.images[0].image_path} alt="Profile" />
              ) : (
                <div className="placeholder">{profile?.nickname?.[0] || user?.email?.[0] || '?'}</div>
              )}
              {isUploadingImage && (
                <div className="loading-overlay">
                  <div className="loading-content">
                    <div className="loading-spinner"></div>
                    Uploading...
                  </div>
                </div>
              )}
              <input
                type="file"
                accept="image/*"
                onChange={handleProfileImageChange}
                style={{ display: 'none' }}
                id="profile-image-input"
                disabled={isUploadingImage || isLoading}
              />
              <label htmlFor="profile-image-input" className="upload-btn">
                {isUploadingImage ? (
                  <>
                    <span className="loading-spinner"></span>
                    {t('dashboard.uploading', 'Uploading...')}
                  </>
                ) : (
                  t('dashboard.changePhoto', 'Change Photo')
                )}
              </label>
            </div>
            
            <div className="profile-details">
              {isEditing ? (
                <div className="profile-form">
                  {isUpdatingProfile && (
                    <div className="loading-overlay">
                      <div className="loading-content">
                        <div className="loading-spinner"></div>
                        Saving changes...
                      </div>
                    </div>
                  )}
                  <div className="form-field">
                    <label htmlFor="fullName-input">
                      {t('dashboard.fullName', 'Full Name')} <span className="required">*</span>
                    </label>
                    <input
                      id="fullName-input"
                      type="text"
                      placeholder={t('dashboard.fullNamePlaceholder', 'Enter your full name')}
                      value={profileForm.fullName}
                      onChange={(e) => handleFormChange('fullName', e.target.value)}
                      disabled={isUpdatingProfile || isLoading}
                      maxLength={100}
                      required
                    />
                    <div className="char-counter">
                      {profileForm.fullName ? profileForm.fullName.length : 0}/100
                    </div>
                  </div>
                  <div className="form-field">
                    <label htmlFor="nickname-input">
                      {t('dashboard.nickname', 'Nickname')} <span className="required">*</span>
                    </label>
                    <input
                      id="nickname-input"
                      type="text"
                      placeholder={t('dashboard.nicknamePlaceholder', 'Enter your nickname')}
                      value={profileForm.nickname}
                      onChange={(e) => handleFormChange('nickname', e.target.value)}
                      disabled={isUpdatingProfile || isLoading}
                      maxLength={50}
                      required
                    />
                    <div className="char-counter">
                      {profileForm.nickname ? profileForm.nickname.length : 0}/50
                    </div>
                  </div>
                  <div className="form-field">
                    <label htmlFor="country-select">
                      {t('dashboard.country', 'Country')} <span className="required">*</span>
                    </label>
                    <div className="country-select-container">
                      {profileForm.country && (
                        <img 
                          src={`/flags/${profileForm.country.toLowerCase()}.webp`} 
                          alt={`${profileForm.country} flag`} 
                          className="country-flag-select"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      )}
                      <select
                        id="country-select"
                        value={profileForm.country}
                        onChange={(e) => handleFormChange('country', e.target.value)}
                        disabled={isUpdatingProfile || isLoading}
                        className="country-select"
                        required
                      >
                        <option value="">
                          {detectedCountry 
                            ? t('dashboard.selectCountry', 'Select your country') 
                            : t('dashboard.detectingCountry', 'Detecting your country...')}
                        </option>
                        {countryList.map((country) => (
                          <option key={country.code} value={country.code}>
                            {country.name} ({country.code})
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="form-field">
                    <label>
                      {t('dashboard.gender', 'Gender')} <span className="required">*</span>
                    </label>
                    <div className="radio-group">
                      <label className="radio-option">
                        <input
                          type="radio"
                          name="gender"
                          value="male"
                          checked={profileForm.gender === 'male'}
                          onChange={(e) => handleFormChange('gender', e.target.value)}
                          disabled={isUpdatingProfile || isLoading}
                          required
                        />
                        <span className="radio-label">{t('dashboard.male', 'Male')}</span>
                      </label>
                      <label className="radio-option">
                        <input
                          type="radio"
                          name="gender"
                          value="female"
                          checked={profileForm.gender === 'female'}
                          onChange={(e) => handleFormChange('gender', e.target.value)}
                          disabled={isUpdatingProfile || isLoading}
                          required
                        />
                        <span className="radio-label">{t('dashboard.female', 'Female')}</span>
                      </label>
                    </div>
                  </div>
                  <div className="form-field">
                    <label htmlFor="dob-input">
                      {t('dashboard.dateOfBirth', 'Date of Birth')} <span className="required">*</span>
                    </label>
                    <input
                      id="dob-input"
                      type="date"
                      value={profileForm.dateOfBirth}
                      onChange={(e) => handleFormChange('dateOfBirth', e.target.value)}
                      disabled={isUpdatingProfile || isLoading}
                      required
                      max={new Date().toISOString().split('T')[0]} // Prevent future dates
                    />
                  </div>
                  <div className="form-field">
                    <label htmlFor="bio-input">
                      {t('dashboard.bio', 'Bio')} <span className="required">*</span>
                    </label>
                    <textarea
                      id="bio-input"
                      placeholder={t('dashboard.bioPlaceholder', 'Tell us about yourself...')}
                      value={profileForm.bio}
                      onChange={(e) => handleFormChange('bio', e.target.value)}
                      disabled={isUpdatingProfile || isLoading}
                      maxLength={1000}
                      required
                    />
                    <div className="char-counter">
                      {profileForm.bio ? profileForm.bio.length : 0}/1000
                    </div>
                  </div>
                  <div className="form-field">
                    <label className="field-label">
                      {t('dashboard.profileVisibility', 'Show Profile Publicly')}
                    </label>
                    <div className="radio-group">
                      <label className="radio-option">
                        <input
                          type="radio"
                          name="isEnabled"
                          value="true"
                          checked={profileForm.isEnabled === true}
                          onChange={() => handleFormChange('isEnabled', true)}
                          disabled={isUpdatingProfile || isLoading}
                        />
                        <span className="radio-label">{t('dashboard.yes', 'Yes')}</span>
                      </label>
                      <label className="radio-option">
                        <input
                          type="radio"
                          name="isEnabled"
                          value="false"
                          checked={profileForm.isEnabled === false}
                          onChange={() => handleFormChange('isEnabled', false)}
                          disabled={isUpdatingProfile || isLoading}
                        />
                        <span className="radio-label">{t('dashboard.no', 'No')}</span>
                      </label>
                    </div>
                    <p className="field-hint">
                      {profileForm.isEnabled 
                        ? t('dashboard.profileVisible', 'Your profile is visible to everyone')
                        : t('dashboard.profileHidden', 'Your profile is hidden from public view')}
                    </p>
                  </div>
                  <div className="form-actions">
                    <button onClick={handleUpdateProfile} disabled={isUpdatingProfile || isLoading}>
                      {isUpdatingProfile ? (
                        <>
                          <span className="loading-spinner"></span>
                          {t('dashboard.saving', 'Saving...')}
                        </>
                      ) : (
                        t('dashboard.save', 'Save')
                      )}
                    </button>
                    <button onClick={() => setIsEditing(false)} disabled={isUpdatingProfile || isLoading}>
                      {t('dashboard.cancel', 'Cancel')}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="profile-display">
                  <h3>{profile?.name || t('dashboard.noNickname', 'No name set')}</h3>
                  <p className="email">{profile?.email}</p>
                  
                  <div className="profile-field">
                    <label className="field-label">{t('dashboard.nickname', 'Nickname')}:</label>
                    <p className="field-value">{profile?.nickname || t('dashboard.noNickname', 'No nickname set')}</p>
                  </div>
                  
                  <div className="profile-field">
                    <label className="field-label">{t('dashboard.gender', 'Gender')}:</label>
                    <p className="field-value">
                      {profile?.gender ? t(`dashboard.${profile.gender}`, profile.gender) : 'Not specified'}
                    </p>
                  </div>
                  
                  <div className="country-display">
                    <label className="field-label">{t('dashboard.country', 'Country')}:</label>
                    {profile?.country ? (
                      <div className="country-value">
                        <img 
                          src={`/flags/${profile.country.toLowerCase()}.webp`} 
                          alt={`${profile.country} flag`} 
                          className="country-flag-display"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                        <span className="country-code">{profile.country}</span>
                      </div>
                    ) : (
                      <span className="no-country">{t('dashboard.noCountry', 'No country set')}</span>
                    )}
                  </div>
                  
                  <div className="profile-field">
                    <label className="field-label">{t('dashboard.dateOfBirth', 'Date of Birth')}:</label>
                    <p className="field-value">
                      {profile?.date_of_birth 
                        ? new Date(profile.date_of_birth).toLocaleDateString(undefined, { 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                          })
                        : 'Not specified'}
                    </p>
                  </div>
                  
                  <div className="bio-container">
                    <label className="bio-label">{t('dashboard.bio', 'Bio')}:</label>
                    <p className="bio">{profile?.message || t('dashboard.noBio', 'No bio set')}</p>
                  </div>
                  
                  <p className="created-at">
                    {t('dashboard.joinedOn', 'Joined on')}: {profile?.created_at ? new Date(profile.created_at).toLocaleDateString() : 'Unknown'}
                  </p>
                  <div className="profile-actions">
                    <button onClick={() => setIsEditing(true)} disabled={isLoading || isUpdatingProfile || isUploadingImage}>
                      {t('dashboard.edit', 'Edit Profile')}
                    </button>
                    <button onClick={() => setShowPreview(true)} className="preview-btn" disabled={isLoading || isUpdatingProfile || isUploadingImage}>
                      {t('dashboard.preview', 'Preview')}
                    </button>
                    <button onClick={() => {
                      setShowShare(true);
                      // Track share modal opened
                      trackEvent('share_modal_opened', {
                        user_id: profile?.uuid,
                        source: 'dashboard',
                        channel: getCurrentChannel()
                      });
                    }} className="share-btn" disabled={isLoading || isUpdatingProfile || isUploadingImage}>
                      {t('dashboard.share', 'Share Profile')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      </div>

      {/* Footer */}
      <Footer />

      {/* Profile Preview Modal */}
      {showPreview && profile && (
        <ProfilePreviewModal
          profile={profile}
          onClose={() => setShowPreview(false)}
        />
      )}

      {/* Share Modal */}
      {showShare && profile && (
        <ShareModal
          isOpen={showShare}
          onClose={() => setShowShare(false)}
          profile={profile}
          profileUrl={generateProfileUrl()}
        />
      )}

      {/* Notification */}
      <Notification
        message={notification.message}
        type={notification.type}
        visible={notification.visible}
        onClose={() => setNotification(prev => ({ ...prev, visible: false }))}
      />
    </div>
  );
};

export default Dashboard;